import AppleIcon from "@mui/icons-material/Apple";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api";
import { persistPortalSession } from "../utils/portalSession";

const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  "1092857712328-o2e9q04mplsgafebgk1bjnng2oi4ufut.apps.googleusercontent.com";
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || "com.elevenplusmagic.app";
const GOOGLE_JS = "https://accounts.google.com/gsi/client?hl=en_GB";
const APPLE_JS = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const TERMS_VERSION = "2026-10-12";
// New families sign up on the main site; this portal login only signs existing accounts in.
const MAIN_SITE_URL = process.env.REACT_APP_MAIN_SITE_URL || "https://11plusmagic.co.uk";
const NO_ACCOUNT_REDIRECT_MS = 4000;

let googleScript;
let appleScript;

function loadScript(src, slot) {
  if (slot.current) return slot.current;
  slot.current = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      if (existing.getAttribute("data-loaded") === "1") resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.setAttribute("data-loaded", "1");
      resolve();
    };
    script.onerror = () => {
      slot.current = null;
      reject(new Error("Could not load sign-in."));
    };
    document.head.appendChild(script);
  });
  return slot.current;
}

const googleSlot = { current: null };
const appleSlot = { current: null };

/**
 * Google and Apple on the student portal login. Login only: an existing student signs in;
 * an email with no account is told so and sent to the main site to sign up.
 */
export default function SocialSignInButtons() {
  const navigate = useNavigate();
  const googleHost = useRef(null);
  // Hidden until the settings load, so the buttons never flash in when a switch is off.
  // One switch each in Admin Settings → System.
  const [googleOn, setGoogleOn] = useState(false);
  const [appleOn, setAppleOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const redirectTimer = useRef(null);

  useEffect(() => () => clearTimeout(redirectTimer.current), []);

  useEffect(() => {
    let cancelled = false;
    api
      .get("public/site-settings", { params: { _: Date.now() } })
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || {};
        const google = data.google_sign_in !== false;
        setGoogleOn(google);
        // An API from before the separate Apple switch: Apple follows Google.
        setAppleOn("apple_sign_in" in data ? data.apple_sign_in !== false : google);
      })
      // Allowed unless the switch is off.
      .catch(() => {
        if (cancelled) return;
        setGoogleOn(true);
        setAppleOn(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const finish = async (path, data) => {
    if (!path) {
      toast.info("Tester accounts must log in via the Admin login page.");
      navigate("/admin-login");
      return;
    }
    if (data.is_new_user) {
      toast.success("Account created. Check your email for your username, passwords, and parent PIN.");
    } else {
      toast.success("Login successful!");
    }
    navigate(path);
  };

  const postCredential = async (endpoint, extra) => {
    setError("");
    setBusy(true);
    try {
      const res = await api.post(endpoint, {
        terms_accepted: true,
        terms_version: TERMS_VERSION,
        login_only: true,
        ...extra,
      });
      const data = res.data?.data ?? {};
      if (!data.access_token) {
        setError("Sign-in failed. Please try again.");
        return;
      }
      const path = persistPortalSession(data);
      await finish(path, data);
    } catch (err) {
      const detail = err?.response?.data?.data || {};
      if (detail.code === "no_account") {
        const message =
          "This account is not in our records. Taking you to 11plusmagic.co.uk to sign up…";
        setError(message);
        toast.info(message, { autoClose: NO_ACCOUNT_REDIRECT_MS });
        window.google?.accounts?.id?.disableAutoSelect?.();
        clearTimeout(redirectTimer.current);
        redirectTimer.current = setTimeout(() => {
          window.location.assign(MAIN_SITE_URL);
        }, NO_ACCOUNT_REDIRECT_MS);
        return;
      }
      setError(
        detail.error ||
          err?.response?.data?.message ||
          "Sign-in failed. Use your username and password.",
      );
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!googleOn || !GOOGLE_CLIENT_ID) return undefined;
    let cancelled = false;
    loadScript(GOOGLE_JS, googleSlot)
      .then(() => {
        if (cancelled || !googleHost.current || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: false,
          callback: ({ credential }) => {
            if (credential) postCredential("auth/google", { credential });
          },
        });
        window.google.accounts.id.disableAutoSelect?.();
        googleHost.current.innerHTML = "";
        window.google.accounts.id.renderButton(googleHost.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "signin_with",
          width: 320,
          shape: "pill",
          locale: "en_GB",
        });
      })
      .catch(() => {
        if (!cancelled) setError("Google sign-in could not load.");
      });
    return () => {
      cancelled = true;
    };
    // postCredential closes over the latest navigate; the button is rendered once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleOn]);

  const signInWithApple = async () => {
    setError("");
    setBusy(true);
    try {
      await loadScript(APPLE_JS, appleSlot);
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: "name email",
        redirectURI: `${window.location.origin}/login`,
        usePopup: true,
      });
      const result = await window.AppleID.auth.signIn();
      const credential = result?.authorization?.id_token;
      if (!credential) {
        setError("Apple sign-in failed. Please try again.");
        return;
      }
      const name = result?.user?.name || {};
      await postCredential("auth/apple", {
        credential,
        given_name: name.firstName || undefined,
        family_name: name.lastName || undefined,
      });
    } catch (err) {
      const appleError = err?.error || err?.message;
      if (appleError === "popup_closed_by_user" || appleError === "user_cancelled_authorize") {
        return;
      }
      if (appleError === "popup_blocked_by_browser") {
        setError("Your browser blocked the Apple sign-in window. Allow popups for this site, then try again.");
        return;
      }
      if (appleError === "invalid_client" || appleError === "invalid_request") {
        setError("Apple sign-in is not set up for this website yet. Use your username and password.");
        return;
      }
      if (!err?.response) {
        setError("Apple sign-in failed. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!googleOn && !appleOn) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 1.5, mb: 2 }}>
      {googleOn ? (
        <Box ref={googleHost} sx={{ display: "flex", justifyContent: "center", minHeight: 44 }} />
      ) : null}
      {appleOn ? (
        <Button
          type="button"
          onClick={signInWithApple}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} sx={{ color: "#3c4043" }} /> : <AppleIcon sx={{ color: "#000", fontSize: 18 }} />}
          sx={{
            alignSelf: "center",
            width: "100%",
            maxWidth: 320,
            height: 40,
            minHeight: 40,
            px: 1.5,
            borderRadius: "999px",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            fontFamily: "Roboto, Arial, sans-serif",
            letterSpacing: "0.25px",
            backgroundColor: "#fff",
            color: "#3c4043",
            border: "1px solid #dadce0",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#f7f8f8", borderColor: "#dadce0", boxShadow: "none" },
          }}
        >
          Sign in with Apple
        </Button>
      ) : null}
      {error ? (
        <Typography sx={{ color: "#D9251A", fontSize: "0.85rem", textAlign: "center", fontWeight: 500 }}>
          {error}
        </Typography>
      ) : null}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "#5A6478", fontSize: "0.85rem" }}>
        <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e0e4f0" }} />
        or use your username
        <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e0e4f0" }} />
      </Box>
    </Box>
  );
}
